import React, {useEffect, useRef, useState} from 'react';
import {Field, FieldProps, useFormikContext} from 'formik';
import {Option} from "../../common/form/ElogSelect";
import Loading, {LoadingContact, LoadingType} from "../../../models/Loading";
import OrderComponent from "../../../models/OrderComponent";
import Autosuggest, {SuggestionsFetchRequestedParams} from "react-autosuggest";
import {apiClient} from "../../../utils/ApiClient";
import {ESTONIA_ID} from "../../../utils/Constants";

interface LoadingsProps {
    countries: Option[];
    orderLoadings: Loading[];
    orderUnloadings: Loading[];
    orderComponents: OrderComponent[];
}

export const DEFAULT_CONTACT: LoadingContact = {
    email: '',
    name: '',
    loading_id: 0,
    phone: '',
    no_email: 0
};
const DEFAULT_LOADING: Loading = {
    order_id: 0,
    country: ESTONIA_ID,
    type: LoadingType.Loading,
    contacts: [{...DEFAULT_CONTACT}]
};

const DEFAULT_SUGGESTIONS = {
    loadings: [
        {
            prevValue: '',
            suggestions: []
        }
    ],
    unloadings: [
        {
            prevValue: '',
            suggestions: []
        }
    ]
}

const Loadings: React.FC<LoadingsProps> = ({
                                               countries,
                                               orderLoadings,
                                               orderUnloadings,
                                               orderComponents
                                           }: LoadingsProps) => {

    const formik = useFormikContext<any>();

    const [loadings, setLoadings] = useState<Loading[]>(orderLoadings ?? []);
    const [unloadings, setUnloadings] = useState<Loading[]>(orderUnloadings ?? []);
    const [requiredFields, setRequiredFields] = useState<any>({});
    const [suggestions, setSuggestions] = useState<any>(DEFAULT_SUGGESTIONS);

    const [clientSuggestions, setClientSuggestions] = useState<Option[]>([]);
    const lastFetchedClient = useRef<{ loadings: string; unloadings: string }>({loadings: '', unloadings: ''});
    const isFetching = useRef(false);

    useEffect(() => {
        if (orderLoadings.length < 1) {
            setLoadings([{...DEFAULT_LOADING}]);
        }

        if (orderUnloadings.length < 1) {
            setUnloadings([{...DEFAULT_LOADING}]);
        }
    }, []);

    useEffect(() => {
        const fixArray = (arr: any[]) => arr.map(item => ({
            ...item,
            country: item && item.country ? item.country : ESTONIA_ID
        }));

        let updated = false;

        const fixedLoadings = fixArray(formik.values.loadings || []);
        if (JSON.stringify(fixedLoadings) !== JSON.stringify(formik.values.loadings)) {
            updated = true;
            setLoadings(fixedLoadings);
            formik.setFieldValue('loadings', fixedLoadings);
        }

        const fixedUnloadings = fixArray(formik.values.unloadings || []);
        if (JSON.stringify(fixedUnloadings) !== JSON.stringify(formik.values.unloadings)) {
            updated = true;
            setUnloadings(fixedUnloadings);
            formik.setFieldValue('unloadings', fixedUnloadings);
        }

        if (!updated && (loadings.length === 0 && (orderLoadings || []).length > 0)) {
            const initLoadings = fixArray(orderLoadings);
            setLoadings(initLoadings);
            formik.setFieldValue('loadings', initLoadings);
        }

        if (!updated && (unloadings.length === 0 && (orderUnloadings || []).length > 0)) {
            const initUnloadings = fixArray(orderUnloadings);
            setUnloadings(initUnloadings);
            formik.setFieldValue('unloadings', initUnloadings);
        }
    }, [formik.values.loadings, formik.values.unloadings]);

    useEffect(() => {
        const getFieldStatus = (componentName: string) => {
            const component = orderComponents.find((comp) => comp.component === componentName);
            return {
                present: !!component,
                mandatory: component?.pivot?.mandatory || false,
            };
        };

        const requiredFieldsObject = {
            Country: getFieldStatus('Country'),
            City: getFieldStatus('City'),
            Address: getFieldStatus('Address'),
            Contact: getFieldStatus('Contact'),
        };

        setRequiredFields(requiredFieldsObject);
    }, [orderComponents]);

    useEffect(() => {
        // Removed hash check that prevented suggestions from being fetched

        const updateSuggestions = async () => {
            if (isFetching.current) return;
            isFetching.current = true;
            try {
                const newSuggestions = {
                    loadings: [...suggestions.loadings],
                    unloadings: [...suggestions.unloadings]
                };

                for (const type of ['loadings', 'unloadings'] as const) {
                    const typeArray = formik.values[type];
                    if (!Array.isArray(typeArray)) continue;

                    for (let index = 0; index < typeArray.length; index++) {
                        const loading = typeArray[index];
                        const prev = suggestions[type][index]?.prevValue;
                        const currentCompany = loading.company?.trim();

                        const existingContactId = formik.values[type]?.[index]?.contacts?.[0]?.id;
                        if (
                            !currentCompany ||
                            (suggestions[type][index]?.prevValue === currentCompany &&
                                lastFetchedClient.current[type] === currentCompany &&
                                suggestions[type][index]?.suggestions?.length > 0 &&
                                existingContactId)
                        ) {
                            continue;
                        }

                        try {
                            const apiResponse = await apiClient.get(`clients/loadingContacts?client=${encodeURIComponent(currentCompany)}`);
                            const contacts = apiResponse.data.map((contact: { id: number; name: string }) => ({
                                label: contact.name,
                                value: contact.id.toString(),
                            }));

                            if (!newSuggestions[type][index]) {
                                newSuggestions[type][index] = {prevValue: '', suggestions: []};
                            }

                            const existing = newSuggestions[type][index]?.suggestions || [];
                            const existingStr = JSON.stringify(existing);
                            const nextStr = JSON.stringify(contacts);

                            if (existingStr !== nextStr) {
                                newSuggestions[type][index].prevValue = currentCompany;
                                newSuggestions[type][index].suggestions = contacts;
                            }

                            lastFetchedClient.current[type] = currentCompany;
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }

                setSuggestions(JSON.stringify(newSuggestions) !== JSON.stringify(suggestions) ? newSuggestions : suggestions);
            } catch (e) {
                console.error(e);
            } finally {
                isFetching.current = false;
            }
        };

        const timeout = setTimeout(() => {
            updateSuggestions();
        }, 300);

        return () => clearTimeout(timeout);
    }, [formik.values.loadings, formik.values.unloadings]);

    const defaultCountry = countries.find(country => country.label === "Eesti")?.value;

    const addLoading = () => {
        const newLoading = {...DEFAULT_LOADING};
        setLoadings([...loadings, newLoading]);
        formik.setFieldValue('loadings', [...formik.values.loadings, newLoading]);
    }

    const addUnloading = () => {
        const newUnloading = {...DEFAULT_LOADING};
        setUnloadings([...unloadings, newUnloading]);
        formik.setFieldValue('unloadings', [...formik.values.unloadings, newUnloading]);
    }

    const addContact = (type: string, index: number): void => {
        const newContact = {...DEFAULT_CONTACT};
        if (type === 'loading') {
            formik.setFieldValue(`loadings[${index}].contacts`, [...(formik.values.loadings[index]?.contacts || []), newContact]);

            const newLoadings = [...loadings];
            const loading = {...newLoadings[index]};
            loading.contacts = [...(loading.contacts || []), newContact];
            newLoadings[index] = loading;
            setLoadings(newLoadings);

        } else if (type === 'unloading') {
            formik.setFieldValue(`unloadings[${index}].contacts`, [...(formik.values.unloadings[index]?.contacts || []), newContact]);

            const newUnloadings = [...unloadings];
            const loading = {...newUnloadings[index]};
            loading.contacts = [...(loading.contacts || []), newContact];
            newUnloadings[index] = loading;
            setUnloadings(newUnloadings);

        }
    }

    const removeLoading = (type: string, index: number): void => {
        if (type === 'loading') {
            const updatedLoadings = formik.values.loadings.filter((_loading: Loading, i: number) => i !== index);
            formik.setFieldValue('loadings', updatedLoadings);
            setLoadings(updatedLoadings);
        } else if (type === 'unloading') {
            const updatedUnloadings = formik.values.unloadings.filter((_unloading: Loading, i: number) => i !== index);
            formik.setFieldValue('unloadings', updatedUnloadings);
            setUnloadings(updatedUnloadings);
        }
    }

    const removeContact = (type: string, index: number, contactIndex: number): void => {
        if (type === 'loading') {
            const updatedContacts = formik.values.loadings[index].contacts.filter((_contact: LoadingContact, i: number) => i !== contactIndex);
            formik.setFieldValue(`loadings[${index}].contacts`, updatedContacts);

            const updatedLoadings = [...loadings];
            updatedLoadings[index].contacts = updatedContacts;
            setLoadings(updatedLoadings);
        } else if (type === 'unloading') {
            const updatedContacts = formik.values.unloadings[index].contacts.filter((_contact: LoadingContact, i: number) => i !== contactIndex);
            formik.setFieldValue(`unloadings[${index}].contacts`, updatedContacts);

            const updatedUnloadings = [...unloadings];
            updatedUnloadings[index].contacts = updatedContacts;
            setUnloadings(updatedUnloadings);
        }
    };

    const fetchClients = async (inputValue: string) => {
        try {
            const apiResponse = await apiClient.get(`suggest/client?search=${inputValue}`);
            const clients: Option[] = apiResponse.data.map((client: { id: number; name: string }) => ({
                label: client.name,
                value: client.id.toString(),
            }));
            setClientSuggestions(clients);
        } catch (error) {
            console.error('Fetch clients error:', error);
        }
    };

    const onClientSuggestionsFetchRequested = ({value}: SuggestionsFetchRequestedParams) => {
        if (value.length > 1) {
            fetchClients(value);
        } else {
            setClientSuggestions([]);
        }
    };

    const onClientSuggestionsClearRequested = () => {
        setClientSuggestions([]);
    };

    const onClientSuggestionSelected = (field: FieldProps<any>['field']) => (event: React.FormEvent<any>, {suggestion}: { suggestion: Option }) => {
        formik.setFieldValue(field.name, suggestion.label);
    };

    const renderSuggestion = (suggestion: Option) => (
        <div>
            {suggestion.label}
        </div>
    );

    const suggestionChange = async (type: string, index: number, value: any) => {

        const existingContact = formik.values[`${type}s`]?.[index]?.contacts?.[0];
        if (!existingContact) return;

        const lastSetId = existingContact.id?.toString();
        if (lastSetId && lastSetId === value) return;

        const response = await apiClient.get(`client-contacts/loading/${value}`);
        const data = response.data;

        if (!!data.id) {
            formik.setFieldValue(`${type}s[${index}].country`, data.loading.country_id || ESTONIA_ID);
            formik.setFieldValue(`${type}s[${index}].city`, data.loading.city);
            formik.setFieldValue(`${type}s[${index}].address`, data.loading.address);

            formik.setFieldValue(`${type}s[${index}].contacts[0].name`, data.name);
            formik.setFieldValue(`${type}s[${index}].contacts[0].email`, data.email);
            formik.setFieldValue(`${type}s[${index}].contacts[0].phone` , data.phone);
            formik.setFieldValue(`${type}s[${index}].contacts[0].no_email`, 0);
            formik.setFieldValue(`${type}s[${index}].contacts[0].id`, data.id);
        }

    }

    const renderLoadingFields = (loading: Loading, index: number, type: 'loading' | 'unloading', deletedCount: number) => {
        if (loading.deleted === true) {
            return (<Field name={`${type}s[${index}].deleted`} type="hidden" value={true}/>);
        }
        return (
            <tr key={`${type}_${index}_loading`} className={`${type}_${loading.id}`}>
                <th>{type === 'loading' ? 'Laadimiskoha' : 'Mahalaadimise'} {index + 1 - deletedCount} aadress:</th>
                <td>
                    {requiredFields?.Country?.present &&
                    <Field name={`${type}s[${index}].country`} as="select"
                           defaultValue={loading.country || defaultCountry}>
                        {countries.map((country) => (
                            <option key={country.value} value={country.value}
                                    selected={country.value === (String(loading.country) ?? null)}>
                                {country.label}
                            </option>
                        ))}
                    </Field>
                    }
                    {requiredFields?.City?.present &&
                    <Field name={`${type}s[${index}].city`} component="input" type="text"
                           placeholder="Postiindeks ja linn"
                           defaultValue={loading.city} autoComplete="off"/>
                    }
                    {requiredFields?.Address?.present &&

                    <span style={{display: 'inline-flex'}}>
                      <Field name={`${type}s[${index}].company`} placeholder="Firma" defaultValue={loading.company}>
                          {({field}: FieldProps<any>) => (
                              <Autosuggest
                                  suggestions={clientSuggestions}
                                  onSuggestionsFetchRequested={onClientSuggestionsFetchRequested}
                                  onSuggestionsClearRequested={onClientSuggestionsClearRequested}
                                  onSuggestionSelected={(event, {suggestion}) => onClientSuggestionSelected(field)(event, {suggestion})}
                                  getSuggestionValue={(suggestion: Option) => suggestion.label}
                                  renderSuggestion={renderSuggestion}
                                  inputProps={{
                                      ...field,
                                      value: typeof field.value === 'string' ? field.value : (loading.company || ''),
                                      onChange: field.onChange,
                                      placeholder: 'Firma',
                                      autoComplete: 'off'
                                  }}
                                  theme={{
                                      container: 'react-autosuggest__container',
                                      suggestionsContainer: (clientSuggestions.length > 0)
                                          ? 'react-autosuggest__suggestions-container visible'
                                          : 'react-autosuggest__suggestions-container',
                                      suggestionsList: 'react-autosuggest__suggestions-list',
                                      suggestion: 'react-autosuggest__suggestion',
                                      suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                                  }}
                              />
                          )}
                      </Field>
                        {suggestions[type + 's'][index]?.suggestions?.length > 0 && (
                            <select onChange={(event) => suggestionChange(type, index, event.target.value)}>
                                <option value="0">Vali olemasolev kontakt</option>
                                {suggestions[type + 's'][index]?.suggestions?.map((contact: any) => (
                                    <option key={contact.value} value={contact.value}>
                                        {contact.label}
                                    </option>
                                ))}
                            </select>
                        )}
                        <Field name={`${type}s[${index}].address`} component="input" type="text"
                               style={{width: '407px'}}
                               placeholder="Aadress" defaultValue={loading.address} autoComplete="off"/>
                      </span>

                    }
                    {index > 0 &&
                    <a href="javascript:void(0);" onClick={() => {
                        removeLoading(type, index);
                    }}>[x]</a>
                    }
                    {loading.id &&
                    <Field name={`${type}s[${index}].id`} type="hidden" value={loading.id}/>
                    }
                </td>
            </tr>
        );
    };

    const renderContactFields = (loading: Loading, index: number, type: 'loading' | 'unloading', deletedCount: number) => {
        if (!requiredFields?.Contact?.present) {
            return;
        }
        if (loading.deleted === true) {
            return null;
        }
        return (
            <>
                {loading.contacts?.map((contact, contactIndex) => {
                    if (contact.deleted === true) {
                        return (<Field key={`${type}_${index}_contact_${contactIndex}`}
                                       name={`${type}s[${index}].contacts[${contactIndex}].deleted`} type="hidden"
                                       value={true}/>);
                    }
                    return (
                        <tr key={`${type}_${index}_contact_${contactIndex}`} className={`${type}_${loading.id}`}>
                            <th>
                                {contactIndex === 0 &&
                                <>
                                    {type === 'loading' ? 'Laadimiskoha' : 'Mahalaadimise'} {index + 1 - deletedCount} kontakt:
                                </>
                                }
                            </th>
                            <td>
                                <Field name={`${type}s[${index}].contacts[${contactIndex}].name`} component="input"
                                       type="text" placeholder="Nimi" defaultValue={contact.name} autoComplete="off"/>
                                <Field name={`${type}s[${index}].contacts[${contactIndex}].email`} component="input"
                                       type="text" placeholder="Email" defaultValue={contact.email} autoComplete="off"/>
                                <Field name={`${type}s[${index}].contacts[${contactIndex}].phone`} component="input"
                                       type="text" placeholder="Telefon" defaultValue={contact.phone}
                                       autoComplete="off"/>
                                <br/>
                                <Field
                                    name={`${type}s[${index}].contacts[${contactIndex}].no_email`}
                                    onChange={(e: any) => {
                                        formik.setFieldValue(`${type}s[${index}].contacts[${contactIndex}].no_email`, e.target.checked)
                                    }}
                                    type="checkbox"
                                />
                                <label>Ära saada kirja</label>
                                <br/>
                                {contactIndex > 0 &&
                                <a href="javascript:void(0);" onClick={() => {
                                    removeContact(type, index, contactIndex);
                                }}>[x]</a>
                                }
                                {contactIndex == 0 &&
                                <a href="javascript:void(0);" onClick={() => {
                                    addContact(type, index);
                                }}>[+]</a>
                                }
                                {contact.id &&
                                <Field name={`${type}s[${index}].contacts[${contactIndex}].id`} type="hidden"
                                       value={contact.id}/>
                                }
                            </td>
                        </tr>
                    );
                })}
            </>
        );
    };

    let deletedLoadingsCount = 0;
    let deletedUnloadingsCount = 0;

    return (<>
        <div className="form">
            <h2><span>Pealelaadimine</span></h2>
            <table className="form">
                {loadings.map((loading, index) => {
                    if (loading.deleted === true) {
                        ++deletedLoadingsCount;
                    }
                    return (
                        <table className="form">
                            <tbody id="loading_list">
                            {renderLoadingFields(loading, index, 'loading', deletedLoadingsCount)}
                            {renderContactFields(loading, index, 'loading', deletedLoadingsCount)}
                            </tbody>
                        </table>
                    )
                })}
            </table>
            <br/>
            <button type="button" onClick={addLoading}><span><strong>Lisa laadimiskoht</strong></span></button>
        </div>
        <div className="form">
            <h2><span>Mahalaadimised</span></h2>
            <table className="form">
                {unloadings.map((loading, index) => {
                    if (loading.deleted === true) {
                        ++deletedUnloadingsCount;
                    }
                    return (
                        <table className="form">
                            <tbody id="loading_list">
                            {renderLoadingFields(loading, index, 'unloading', deletedUnloadingsCount)}
                            {renderContactFields(loading, index, 'unloading', deletedUnloadingsCount)}
                            </tbody>
                        </table>
                    )
                })}
            </table>
            <br/>
            <button type="button" onClick={addUnloading}><span><strong>Lisa laadimiskoht</strong></span></button>
        </div>
    </>);
}

export default Loadings;